/**
 * Centralized grade tier + color tokens.
 *
 * Before this module the project had four near-duplicate
 * `gradeColor`/`fillColor` helpers (HomeKpis, HomeAtRiskList,
 * HomeAvgByClassroom) plus a scale-aware `tierForGrade` in
 * `lib/grade.ts`. Changing the palette or
 * threshold meant updating four files and hoping you found them all.
 * This file is the single source of truth; everywhere that paints a
 * grade by its health should import from here.
 *
 * Two tier "modes" live side by side because they answer different
 * questions:
 *
 *   - `student` (the existing behaviour) — "did this student pass?".
 *     Anchored on the Mexican secondary-school passing mark of 6.0
 *     on a 0-10 scale. Thresholds: 75% / 60% of the scale max
 *     (so on 0-10: high ≥ 7.5, mid ≥ 6.0, low otherwise).
 *
 *   - `group`  (new, 2026-05-21) — "is this classroom healthy?".
 *     For the sidebar MIS SALONES pill and the dashboard
 *     classroom-average list. A 6.5 individual grade is "passing" but
 *     a classroom averaging 6.5 means something is going wrong, so
 *     the group tier is stricter. Thresholds: 85% / 75% of the scale
 *     max (so on 0-10: high ≥ 8.5, mid ≥ 7.5, low otherwise; on 0-100:
 *     high ≥ 85, mid ≥ 75, low otherwise).
 *
 * Both modes share one tier vocabulary ("high" | "mid" | "low" |
 * "none") and one color palette, so a UI consuming the helpers stays
 * consistent visually even when individual surfaces switch modes.
 */

import type { GradeScale } from "@/types"

export type GradeTier = "high" | "mid" | "low" | "none"

export type GradeTierMode = "student" | "group"

// Thresholds expressed as fractions of the scale max so the helpers
// work for both 0-10 and 0-100 without duplicating numbers. Keep the
// upper threshold first — readers expect "best case first" when
// scanning a config block.
export const GRADE_TIER_THRESHOLDS: Record<GradeTierMode, { high: number; mid: number }> = {
  student: { high: 0.75, mid: 0.6 },
  group: { high: 0.85, mid: 0.75 },
}

// Tailwind class tokens, one per tier. Change a color here and it
// propagates to every grade pill / KPI / chart bar in the app.
export const GRADE_TIER_TEXT_CLASS: Record<GradeTier, string> = {
  high: "text-success-ink",
  mid: "text-attention-ink",
  low: "text-danger-ink",
  none: "text-ink-muted",
}

export const GRADE_TIER_BG_CLASS: Record<GradeTier, string> = {
  high: "bg-success",
  mid: "bg-attention",
  low: "bg-danger",
  none: "bg-border",
}

// Softer surface tones for pills / chips (text + background pair).
// Use when the tier sits next to other UI and we don't want it to
// scream. Keep the text token strong enough to stay readable on the
// light bg in light mode and the dark bg in dark mode.
export const GRADE_TIER_PILL_CLASS: Record<GradeTier, string> = {
  high: "bg-success-soft text-success-ink",
  mid: "bg-attention-soft text-attention-ink",
  low: "bg-danger-soft text-danger-ink",
  none: "bg-inset text-ink-muted",
}

const scaleMax = (scale: GradeScale): number => (scale === "0-100" ? 100 : 10)

/**
 * Classify a grade value into a tier. `mode` defaults to "student"
 * (preserves the historical behaviour of `tierForGrade`); the
 * sidebar + group-average dashboards pass "group" for a stricter
 * health read.
 */
export function gradeTier(
  value: number | null | undefined,
  scale: GradeScale = "0-10",
  mode: GradeTierMode = "student",
): GradeTier {
  if (value === null || value === undefined || value <= 0) return "none"
  const max = scaleMax(scale)
  const { high, mid } = GRADE_TIER_THRESHOLDS[mode]
  if (value >= max * high) return "high"
  if (value >= max * mid) return "mid"
  return "low"
}

export const gradeTierTextClass = (tier: GradeTier): string => GRADE_TIER_TEXT_CLASS[tier]
export const gradeTierBgClass = (tier: GradeTier): string => GRADE_TIER_BG_CLASS[tier]
export const gradeTierPillClass = (tier: GradeTier): string => GRADE_TIER_PILL_CLASS[tier]
